import { useState, useEffect, useRef } from 'react';
import { User } from '@/types/models';
import { config, getMaxPixelQuota } from '@/lib/config';

interface PixelQuotaDisplayComponentProps {
  user: User | null;
  pendingPixelsCount?: number;
  pendingPixelsNeedingQuota?: number;
  shouldFlash?: boolean;
  onFlashComplete?: () => void;
}

export const PixelQuotaDisplayComponent: React.FC<PixelQuotaDisplayComponentProps> = ({
  user,
  pendingPixelsCount = 0,
  pendingPixelsNeedingQuota = 0,
  shouldFlash: externalShouldFlash = false,
  onFlashComplete,
}) => {
  const [shouldFlash, setShouldFlash] = useState(false);
  const prevOverQuotaRef = useRef(false);
  const hasFlashedRef = useRef(false);

  const maxQuota = getMaxPixelQuota();
  // Calculate remaining quota considering pending pixels that need quota
  // Allow negative values to show how much over quota (e.g., -2)
  const remainingQuota = user ? user.pixelQuota - pendingPixelsNeedingQuota : 0;
  const isOverQuota = user
    ? pendingPixelsNeedingQuota > user.pixelQuota
    : false;

  // Trigger flash animation when quota is exceeded (from external signal or when going over quota)
  useEffect(() => {
    if (externalShouldFlash) {
      // External signal to flash - trigger flash animation
      setShouldFlash(true);
      const timer = setTimeout(() => {
        setShouldFlash(false);
        if (onFlashComplete) {
          onFlashComplete();
        }
      }, 800); // Flash for 600ms (3 pulses at 200ms each)
      return () => clearTimeout(timer);
    }
  }, [externalShouldFlash, onFlashComplete]);

  // Trigger flash when quota is exceeded (first time only, even if circle is empty)
  useEffect(() => {
    if (isOverQuota && !prevOverQuotaRef.current) {
      // Quota just exceeded - trigger flash animation once
      setShouldFlash(true);
      hasFlashedRef.current = true;
      const timer = setTimeout(() => {
        setShouldFlash(false);
      }, 600); // Flash for 600ms (3 pulses at 200ms each)
      return () => clearTimeout(timer);
    }
    // Reset flash flag when quota is no longer exceeded
    if (!isOverQuota && prevOverQuotaRef.current) {
      hasFlashedRef.current = false;
    }
    prevOverQuotaRef.current = isOverQuota;
  }, [isOverQuota]);

  if (!user) return null;

  // Calculate breakdown of changes
  // Ensure freeChanges never goes negative
  // const freeChanges = Math.max(0, pendingPixelsCount - pendingPixelsNeedingQuota);
  // const quotaNeeded = pendingPixelsNeedingQuota;

  // If over quota or flashing, show full circle (100% filled) in red
  const showFullRed = isOverQuota || shouldFlash;
  const percentage = showFullRed ? 100 : (remainingQuota / maxQuota) * 100;

  // Determine color based on percentage and over-quota state
  const getColor = () => {
    if (showFullRed) return '#ef4444'; // red when over quota or flashing
    if (percentage <= 10) return '#ef4444'; // red
    if (percentage <= 25) return '#eab308'; // yellow
    return '#929bc9'; // default blue
  };

  const color = getColor();
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = showFullRed
    ? 0 // Full circle when over quota or flashing
    : circumference - (percentage / 100) * circumference;

  // Format change breakdown text
  const getChangeText = () => {
    if (pendingPixelsCount === 0) {
      return `+${config.defaultPixelQuota}/day`;
    }

    return `${pendingPixelsCount} değişiklik`;
  };

  return (
    <div className="pixel-quota-display-component flex items-center gap-3">
      {/* Circular Progress Bar */}
      <div className="relative w-10 h-10">
        <svg
          className={`w-10 h-10 transform -rotate-90 scale-y-[-1] ${isOverQuota ? 'animate-pulse' : shouldFlash ? 'animate-pulse' : ''}`}
        >
          {/* Background circle */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            className="stroke-gray-300 dark:stroke-white/10"
            strokeWidth="3"
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            stroke={color}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
      </div>

      {/* Text info */}
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-700 dark:text-[#929bc9]">
          {remainingQuota} / {maxQuota}
        </span>
        <span className="text-xs text-gray-600 dark:text-[#929bc9] dark:opacity-70">
          {getChangeText()}
        </span>
      </div>
    </div>
  );
};

